import AppContext from 'App.context';
import { AxiosError } from 'axios';
import LoadingScreen from 'components/LoadingScreen/LoadingScreen';
import UserRoleUnauthorizedScreen from 'components/UserRoleUnauthorizedScreen/UserRoleUnauthorizedScreen';
import { WSO2AuthLocalStorageNames } from 'enuns/WSO2AuthLocalStorageNames';
import { getApis } from 'library/Apis';
import { AssessorRoutePath, LogoutRoutePath } from 'library/Routes';
import { UserManager } from 'oidc-client';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Route, RouteProps, useLocation } from 'react-router';
import { bindActionCreators, Dispatch } from 'redux';
import { authActionsLogin, authActionsLoginByToken, authActionsLogout } from 'store/modules/AuthActions';
import { IAuthReducer, IAuthUserReducer } from 'store/modules/AuthReducer';
import { IReducer } from 'store/Reducer';
import { isAssociadoProfile, isAssessorProfile } from 'utils/AuthUtils';
import { getAxiosError } from 'utils/AxiosErrorHandler';
import {
  getWSO2AccessTokenInPath,
  getWSO2IdTokenInPath,
  getWSO2LocalStorageAccessToken,
  getWSO2LocalStorageIdToken,
  getWSO2UserManagerSettings
} from 'utils/WSO2AuthUtils';

export interface IPrivateRoute extends RouteProps {
  auth: IAuthReducer;
  authActionsLogin?: (
    accessToken: string,
    callbackSuccess?: (user: IAuthUserReducer) => void,
    callbackError?: (error: any) => void
  ) => void;
  authActionsLoginByToken?: (
    accessToken: string,
    idToken: string,
    callbackSuccess?: (user: IAuthUserReducer) => void,
    callbackError?: (error: AxiosError | null) => void
  ) => void;
  authActionsLogout?: Function;
}

const PrivateRoute = ({
  auth,
  path,
  component,
  exact,
  children,
  authActionsLogout,
  authActionsLoginByToken,
  authActionsLogin,
}: IPrivateRoute) => {
  let location = useLocation();
  const { associadoManager } = useContext(AppContext);
  const [initialized, setInitialized] = useState(false);
  const [perfilInvalido, setPerfilInvalido] = useState(false);
  const [perfilError, setPerfilError] = useState<string>();

  let ldapUserManager: UserManager = new UserManager(getWSO2UserManagerSettings(getApis().auth));

  const inLogoutPath = useCallback((): boolean => {
    if (location.pathname.indexOf(LogoutRoutePath) > -1) {
      return true;
    }
    return false;
  }, [location]);

  const inAssessorPath = useCallback((): boolean => {
    if (location.pathname.indexOf(AssessorRoutePath.substr(0, AssessorRoutePath.length - 1)) > -1) {
      return true;
    }
    return false;
  }, [location]);

  const clearHrefHash = () => {
    const hashIndex: number = window.location.href.indexOf('#');
    if (hashIndex > -1) {
      window.history.replaceState(undefined, '', window.location.href.split('#')[0]);
    }
  };

  const toPertencerLogout = useCallback(() => {
    // rotina logout assessor
    const idToken: string | null = getWSO2IdTokenInPath() || localStorage.getItem(WSO2AuthLocalStorageNames.IdToken);
    const redirectUri: string = window.location.href;
    let logoutUrl: string;
    if (idToken) {
      logoutUrl = getApis().auth.wso2LogoutUrl.split('#1#').join(idToken) + getApis().auth.logofRedirectUrl;
      window.localStorage.clear();
      window.localStorage.setItem(WSO2AuthLocalStorageNames.RedirectUri, redirectUri || '');
      window.location.href = logoutUrl;
      ldapUserManager.clearStaleState();
    }
  }, [ldapUserManager]);

  const toLogout = useCallback(() => {
    if (auth.logged && auth.usuario) {
      if (isAssessorProfile(auth.usuario.perfil) || getWSO2AccessTokenInPath()) {
        toPertencerLogout();
      } else if (isAssociadoProfile(auth.usuario.perfil)) {
        associadoManager && associadoManager.signoutRedirect();
        window.localStorage.clear();
      }
    } else {
      if (getWSO2AccessTokenInPath()) {
        toPertencerLogout();
      } else {
        associadoManager && associadoManager.signoutRedirect();
        window.localStorage.clear();
      }
    }
  }, [auth, toPertencerLogout, associadoManager]);

  const pertencerLogin = useCallback(() => {
    if (!getWSO2AccessTokenInPath() && !getWSO2LocalStorageAccessToken()) {
      ldapUserManager.signinRedirect();
    } else {
      if (typeof authActionsLoginByToken === 'function') {
        authActionsLoginByToken(
          getWSO2AccessTokenInPath() || getWSO2LocalStorageAccessToken() || '',
          getWSO2IdTokenInPath() || getWSO2LocalStorageIdToken() || '',
          (data: IAuthUserReducer) => {
            clearHrefHash();
            setInitialized(true);
          },
          (error: AxiosError | null) => {
            if (error) {
              if (error.response) {
                setPerfilError(getAxiosError(error) || undefined);
              } else {
                setPerfilInvalido(true);
              }
              // window.alert("O seu usário não possui nenhum perfil válido.");
              // toPertencerLogout();
            } else {
              setPerfilInvalido(true);
            }
            setInitialized(true);
          }
        );
      }
    }
  }, [authActionsLoginByToken, ldapUserManager]);

  const associadoLogin = useCallback(async () => {
    if (!associadoManager) return;
    const associado = await associadoManager.getUser();
    if (associado && authActionsLogin) {
      authActionsLogin(associado.id_token, undefined, (error: any) => {
        setPerfilInvalido(true);
      });
    } else {
      toLogout();
    }
    setInitialized(true);
  }, [authActionsLogin, associadoManager, toLogout]);

  // eslint-disable-next-line
  useEffect(() => {
    if (!auth.logged) {
      if (inAssessorPath() || getWSO2LocalStorageAccessToken()) {
        pertencerLogin();
      } else {
        associadoLogin();
      }
    }
  }, [inAssessorPath, auth.logged, pertencerLogin, associadoLogin]);

  if ((!auth.logged || !auth.usuario) && !initialized) {
    // Mostra view enquanto a autenticação é verificada
    return <LoadingScreen />;
  }

  if (perfilInvalido || perfilError) {
    // Mostra view com perfil LDAP inválido.
    return <UserRoleUnauthorizedScreen label={perfilError || undefined} toLogout={toLogout} />;
  }

  if (auth.logged && auth.usuario) {
    //logado
    if (inLogoutPath()) {
      toLogout();
    }
  }

  return <Route path={path} component={component} exact={exact} children={children} />;
};

const mapStateToProps = (state: IReducer) => {
  return {
    auth: state.auth,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      authActionsLoginByToken,
      authActionsLogout,
      authActionsLogin,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute);
